// ========================================
// INVOICE SERVICE - CLEAN IMPLEMENTATION
// Zentrale Rechnungserstellung und -verwaltung
// ========================================

import isoStandardIdGenerator from '../utils/ISOStandardIdGenerator';
import { compareInvoiceNumbers } from '../utils/invoiceNumberFormat';
import invoiceCalculationService from './InvoiceCalculationService';
import { getCurrentBusinessId, readBestBusinessMergedPayload } from '../utils/sharedCore';
import { readPurchasesSyncForBusiness } from '../utils/purchasesReadSource';
import { getInvoicesRepository } from '../app/di/container';
import {
  areInvoicesEquivalent,
  indexBaseInvoices,
  invoicesFingerprint,
  isConsolidatedInvoiceRecord,
  resolveLiveChildInvoice as resolveLiveChildInvoiceShared,
  refMatchesInvoice
} from '../domain/purchases/consolidatedInvoiceRefs';
import { mergeEntitiesById } from '../utils/businessStateSync';
import { resolveProductId, sameProductId, canonicalProductId } from '../utils/productIdUtils';
import { normalizeProductSku } from '../utils/frenchIdGenerator';
import { resolveInvoiceRemainingAmount } from '../domain/suppliers/balance';

class InvoiceService {
  constructor() {
    this.processedTransactions = new Set();
  }

  /**
   * Erstellt Rechnungen aus Transaktionen (Purchases/Orders)
   */
  async createInvoicesFromTransactions(transactions, dataContext) {
    const { setSupermarketInvoices, suppliers, supermarketInvoices, products, purchases } = dataContext;
    // Keep a reference to products for name lookup during normalization
    this.productsRef = Array.isArray(products) ? products : [];
    // debug removed

    // Lightweight name cache built from purchases (does not mutate pages; uses existing cached data)
    this.purchaseNameMap = new Map(); // key: String(productId) -> name
    try {
      const addName = (pid, nm) => {
        if (!pid || !nm) return;
        const key = String(pid);
        if (!this.purchaseNameMap.has(key) && !this.isGenericProductName(nm)) {
          this.purchaseNameMap.set(key, nm);
        }
      };
      (Array.isArray(purchases) ? purchases : []).forEach(p => {
        const lines = Array.isArray(p.items) ? p.items : (Array.isArray(p.products) ? p.products : (Array.isArray(p.lines) ? p.lines : []));
        lines.forEach(li => {
          const nested = li.product || li.article || li.item || {};
          const rawPid =
            li.productId || li.id || li.product_id || li.productID || nested.id || li.sku || li.code || nested.sku;
          const pid =
            resolveProductId(rawPid, this.productsRef) ??
            (li.productId || li.id || li.product_id || li.productID || nested.id);
          const nm = li.productName || li.product_name || li.produit || li.name || li.nom || li.titre || li.designation || li.libelle || li.label || li.desc || li.description || nested.name || nested.designation;
          addName(pid, nm);
        });
      });
      // debug removed
    } catch (e) {
      // debug removed
    }
    
    // debug removed
    
    const newInvoices = [];
    
    for (const transaction of transactions) {
      try {
        // Prüfe ob bereits verarbeitet
        if (this.processedTransactions.has(transaction.id)) {
          continue;
        }

        // ENHANCED: Robuste Duplikat-Prüfung mit mehreren Kriterien
        const existingInvoice = supermarketInvoices?.find(inv => {
          // 1. Exakte ID-Übereinstimmung
          const exactIdMatch = inv.originalTransactionId === transaction.id;
          
          // 2. Prüfe ob Purchase-ID/Order-ID übereinstimmt (falls Order zu Purchase wurde)
          const purchaseIdMatch = transaction.purchaseId && (
            inv.originalTransactionId === transaction.purchaseId ||
            inv.notes?.includes(transaction.purchaseId) ||
            inv.description?.includes(transaction.purchaseId)
          );
          const orderIdMatch = transaction.orderId && (
            inv.originalTransactionId === transaction.orderId ||
            inv.notes?.includes(transaction.orderId) ||
            inv.description?.includes(transaction.orderId)
          );
          
          // 4. Prüfe ob Transaktions-ID in Notes oder Description enthalten ist
          const notesMatch = inv.notes?.includes(transaction.id) || false;
          const descriptionMatch = inv.description?.includes(transaction.id) || false;
          
          // 6. Prüfe ob Purchase-ID in verschiedenen Feldern enthalten ist
          const originalTransactionIdStr = String(inv.originalTransactionId || '');
          const invoiceNumberStr = String(inv.invoiceNumber || '');
          const transactionIdStr = String(transaction.id || '');
          const fieldMatch = (originalTransactionIdStr && originalTransactionIdStr.includes(transactionIdStr)) ||
                            (invoiceNumberStr && invoiceNumberStr.includes(transactionIdStr));
          
          const isDuplicate =
            exactIdMatch ||
            purchaseIdMatch ||
            orderIdMatch ||
            notesMatch ||
            descriptionMatch ||
            fieldMatch;
          
          // Duplicate check - no logging needed
          
          return isDuplicate;
        });
        
        if (existingInvoice) {
          continue;
        }

        const supplier = this.resolveSupplier(transaction.supplierId, suppliers);
        if (!supplier) {
          console.warn(
            '[AUTO INVOICE] Fournisseur introuvable:',
            transaction.supplierId,
            'achat:',
            transaction.id
          );
          continue;
        }

        // Erstelle Rechnung
        const invoice = this.createInvoice(transaction, supplier);
        if (invoice) {
          newInvoices.push(invoice);
          this.processedTransactions.add(transaction.id);
        }
      } catch (error) {
        console.warn(
          '[AUTO INVOICE] Transaction processing failed:',
          transaction?.id,
          error?.message || error
        );
      }
    }

    if (newInvoices.length > 0) {
      let mergedInvoices = this.removeDuplicateInvoices([
        ...(supermarketInvoices || []),
        ...newInvoices
      ]);

      mergedInvoices = this.consolidateInvoicesInMemory(mergedInvoices);

      const businessId = dataContext.businessId || getCurrentBusinessId();
      if (businessId) {
        const stored = await getInvoicesRepository().mutatePayload(
          businessId,
          (parsed) => {
            parsed.supermarketInvoices = mergedInvoices;
            return parsed;
          },
          { authoritativeFields: ['supermarketInvoices'] }
        );
        if (stored?.supermarketInvoices) {
          mergedInvoices = stored.supermarketInvoices;
        }
      }

      if (setSupermarketInvoices) {
        setSupermarketInvoices(mergedInvoices);
      }
    }

    return newInvoices;
  }

  /**
   * Persistiert Rechnungen atomar (nach autoConsolidate-React-Updates ggf. erneut aufrufen).
   */
  async persistSupermarketInvoices(invoices, businessId = null) {
    const id = businessId || getCurrentBusinessId();
    if (!id || !Array.isArray(invoices)) return;
    // Always ≤1 CON parent per supplier before write (stops 2×/3× shells sticking in KV)
    const consolidated = this.consolidateInvoicesInMemory(invoices);
    await getInvoicesRepository().persistSupermarketInvoices(id, consolidated);
    return consolidated;
  }

  /**
   * Merge by id then consolidate — use after load/KV refresh so old CON shells
   * cannot pile up next to newly minted ones.
   */
  mergeAndConsolidateInvoices(prev = [], loaded = [], idKeys = ['id']) {
    const merged = mergeEntitiesById(
      Array.isArray(prev) ? prev : [],
      Array.isArray(loaded) ? loaded : [],
      idKeys
    );
    return this.consolidateInvoicesInMemory(merged);
  }

  /**
   * Findet Lieferant anhand verschiedener ID-Formate oder Name
   * PRIORITÄT: IDs (Primärschlüssel) > Namen (Fallback)
   */
  findSupplier(supplierId, suppliers) {
    return this.resolveSupplier(supplierId, suppliers);
  }

  resolveSupplier(supplierId, suppliers) {
    if (supplierId == null || supplierId === '') return null;
    const list = Array.isArray(suppliers) ? suppliers : [];
    const key = String(supplierId).trim();

    const foundById = list.find(
      (s) =>
        String(s.id) === key ||
        String(s.supplierId) === key ||
        String(s._id) === key
    );
    if (foundById) return foundById;

    const keyLower = key.toLowerCase();
    const foundByName = list.find((s) => {
      const name = (s.name || s.supplierName || s.company || '').toLowerCase().trim();
      return name.length > 0 && name === keyLower;
    });
    if (foundByName) return foundByName;

    return null;
  }

  /**
   * Erstellt eine Rechnung aus einer Transaktion
   */
  createInvoice(transaction, supplier) {
    const invoiceNumber = this.generateInvoiceNumber(transaction);
    
    // Stelle sicher, dass der Lieferantenname korrekt extrahiert wird
    const supplierName = supplier?.name || 
                        supplier?.supplierName || 
                        supplier?.company || 
                        (supplier?.id ? `Fournisseur ${supplier.id}` : 'Unknown Supplier');
    
    // ✅ WICHTIG: Stelle sicher, dass invoiceDate immer gesetzt ist (für FIFO-Sortierung)
    const invoiceDate = transaction.purchaseDate || 
                        transaction.orderDate || 
                        transaction.date ||
                        transaction.createdAt ||
                        new Date().toISOString();
    
    // ✅ WICHTIG: Stelle sicher, dass invoiceNumber immer gesetzt ist
    const finalInvoiceNumber = invoiceNumber || 
                                `INV-${transaction.type?.toUpperCase() || 'UNK'}-${Date.now()}`;
    
    return {
      id: `invoice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      invoiceNumber: finalInvoiceNumber, // Immer gesetzt
      supplierId: transaction.supplierId,
      supplierName: supplierName,
      supplierAddress: supplier?.address || '',
      supplierPhone: supplier?.phone || '',
      totalAmount: transaction.totalAmount || transaction.total || 0,
      paidAmount: 0,
      status: 'pending',
      type: transaction.type === 'purchase' ? 'purchase' : 'order',
      originalTransactionId: transaction.id,
      purchaseId: transaction.purchaseId || transaction.id,
      originalTransactionType: transaction.type,
      createdAt: new Date().toISOString(),
      invoiceDate: invoiceDate, // Immer gesetzt für FIFO-Sortierung
      notes: `Auto-generated invoice for ${transaction.type} ${transaction.id}`,
      isConsolidated: false,
      items: this.generateInvoiceItems(transaction)
    };
  }

  /**
   * Generiert eindeutige Rechnungsnummer - ISO-Standard Konform
   */
  generateInvoiceNumber(transaction) {
    try {
      return isoStandardIdGenerator.generateNormalInvoiceNumber();
    } catch (error) {
      // Fallback: Alte Methode
      const type = transaction.type === 'purchase' ? 'PUR' : 'ORD';
      const fallbackNumber = `INV-${type}-${transaction.id}`;
      return fallbackNumber;
    }
  }

  /**
   * Generiert Rechnungspositionen
   */
  generateInvoiceItems(transaction) {
      // debug removed

    const rawItems = this.extractRawItems(transaction);
    if (rawItems.length > 0) {
      // debug removed
      const normalized = rawItems.map((item, idx) => {
        const norm = this.normalizeItem(item);
        // If name still generic, try to read it directly from achat item fields
        const looksGeneric = this.isGenericProductName(norm.productName);
        if (looksGeneric) {
          const rawName = item.productName || item.product_name || item.produit || item.designation || item.libelle || item.nom || item.titre || item.desc || item.description || (item.product && (item.product.name || item.product.designation));
          if (rawName) {
            norm.productName = rawName;
          }
        }
        return norm;
      });
      // debug removed
      return normalized;
    }

    // Fallback: single summary line
    return [{
      productId: transaction.id,
      productName: `${transaction.type} ${transaction.id}`,
      quantity: 1,
      unitPrice: transaction.totalAmount || transaction.total || 0,
      totalPrice: transaction.totalAmount || transaction.total || 0
    }];
  }

  /**
   * Detects generic placeholder names that should be replaced
   */
  isGenericProductName(name) {
    if (!name || typeof name !== 'string') return true;
    if (name === 'Article') return true;
    if (/^Achat\s/i.test(name)) return true;
    return false;
  }

  /**
   * Extract possible items arrays from different transaction shapes
   */
  extractRawItems(transaction) {
    if (!transaction) return [];

    // Direct items
    if (Array.isArray(transaction.items) && transaction.items.length > 0) {
      // debug removed
      // Some sources put nested arrays or child objects with their own items arrays
      const items = [];
      transaction.items.forEach(entry => {
        if (Array.isArray(entry)) {
          items.push(...entry);
        } else if (entry && Array.isArray(entry.items)) {
          items.push(...entry.items);
        } else {
          items.push(entry);
        }
      });
      return items;
    }

    // Some sources may use 'products' or 'lines'
    if (Array.isArray(transaction.products) && transaction.products.length > 0) {
      // debug removed
      return transaction.products;
    }
    if (Array.isArray(transaction.lines) && transaction.lines.length > 0) {
      // debug removed
      return transaction.lines;
    }

    // Our grouping step collects each purchase/order record into items[]
    // If those records themselves contain a single product data, return them
    if (Array.isArray(transaction.items) && transaction.items.length > 0) {
      return transaction.items;
    }

    return [];
  }

  /**
   * Normalize a raw line item from various shapes to a standard schema
   */
  normalizeItem(item) {
    // Defensive logging for unknown shapes
    if (!item || typeof item !== 'object') {
      return { productId: undefined, productName: 'Article', quantity: 1, unitPrice: 0, totalPrice: 0 };
    }
    const nested = item.product || item.article || item.item || {};
    
    const toNumber = (v, fallback = 0) => {
      if (v === null || v === undefined || v === '') return fallback;
      const num = typeof v === 'string' ? parseFloat(v.replace(/[^0-9.-]/g, '')) : Number(v);
      return isNaN(num) ? fallback : num;
    };
    
    const rawProductId =
      item.productId ||
      item.id ||
      item.product_id ||
      item.productID ||
      nested.id ||
      item.sku ||
      item.code ||
      nested.sku ||
      undefined;
    // SKU/code nur als Lookup → echte Katalog-productId (nie SKU als Identity speichern)
    const productId =
      resolveProductId(rawProductId, this.productsRef) ??
      (item.productId || item.id || item.product_id || item.productID || nested.id || undefined);
    let productName = item.productName || item.name || item.designation || item.label || item.libelle || item.article || nested.name || nested.designation || 'Article';
    const quantity = toNumber(item.quantity || item.qty || item.quantityPurchased || item.quantite || item.qte || item.qteAchetee || nested.qty || 1, 1);
    const unitPrice = toNumber(item.unitPrice || item.price || item.unit_price || item.prix || item.prixAchat || item.prixUnitaire || item.pu || nested.price || nested.prix || 0, 0);
    let totalPrice = toNumber(item.totalPrice || item.total || item.amount || item.montant || nested.total, 0);
    if (totalPrice === 0) totalPrice = quantity * unitPrice;

    // If name looks generic (e.g., 'Achat ACHATxxxxx' or 'Article'), try resolve from products list
    const looksGeneric = typeof productName === 'string' && (/^Achat\s/i.test(productName) || productName === 'Article');
    if (looksGeneric && productId && Array.isArray(this.productsRef)) {
      const found = this.productsRef.find(p => String(p.id) === String(productId));
      // debug removed
      if (found) {
        const resolved = found.name || found.designation || found.label || found.libelle;
        if (resolved) {
          productName = resolved;
        }
      }
    }

    // Fallback #2: purchase name cache
    if (this.isGenericProductName(productName) && productId && this.purchaseNameMap instanceof Map) {
      const nm = this.purchaseNameMap.get(String(productId));
      if (nm) {
        productName = nm;
      }
    }

    // SKU immer über productId aus dem Katalog (PROD001…999 / PROD1000…9999)
    let sku = '';
    if (productId && Array.isArray(this.productsRef)) {
      const found = this.productsRef.find((p) =>
        sameProductId(canonicalProductId(p), productId)
      );
      if (found?.sku) {
        sku = normalizeProductSku(found.sku);
      }
    }
    if (!sku) {
      sku = normalizeProductSku(item.sku || item.code || nested.sku || '');
    }

    return { productId, productName, sku, quantity, unitPrice, totalPrice };
  }

  flattenToBaseInvoices(invoices) {
    return this.removeDuplicateInvoices(
      (invoices || []).filter((invoice) => !this.isConsolidatedInvoice(invoice))
    );
  }

  areInvoicesEquivalent(a, b) {
    return areInvoicesEquivalent(a, b);
  }

  invoicesFingerprint(invoices) {
    return invoicesFingerprint(invoices);
  }

  resolveLiveChildInvoice(ref, allInvoices = []) {
    return resolveLiveChildInvoiceShared(ref, allInvoices);
  }

  /**
   * Refresh consolidated `originalInvoices` from live Achat invoices (by id / purchaseId).
   * Idempotent: returns same array/object refs when nothing changed.
   */
  syncConsolidatedOriginalRefs(invoices) {
    const list = Array.isArray(invoices) ? invoices : [];
    if (list.length === 0) return list;

    const indexes = indexBaseInvoices(list);
    let listChanged = false;

    const nextList = list.map((inv) => {
      if (!this.isConsolidatedInvoice(inv) || !Array.isArray(inv.originalInvoices)) {
        return inv;
      }
      let changed = false;
      const originalInvoices = inv.originalInvoices.map((ref) => {
        if (!ref) return ref;
        const live = resolveLiveChildInvoiceShared(ref, list, indexes);
        if (!live) return ref;
        const purchaseId = live.purchaseId || live.originalTransactionId || ref.purchaseId || null;
        const invoiceNumber = live.invoiceNumber || ref.invoiceNumber || live.id;
        const invoiceDate = live.invoiceDate || live.createdAt || ref.invoiceDate;
        const amount = Number(live.totalAmount ?? live.total ?? ref.amount ?? 0) || 0;
        const paidAmount = Number(live.paidAmount ?? ref.paidAmount ?? 0) || 0;
        const prevAmount = Number(ref.amount ?? 0) || 0;
        const prevPaid = Number(ref.paidAmount ?? 0) || 0;
        if (
          String(live.id) !== String(ref.id) ||
          String(invoiceNumber) !== String(ref.invoiceNumber || '') ||
          String(invoiceDate || '') !== String(ref.invoiceDate || '') ||
          amount !== prevAmount ||
          paidAmount !== prevPaid ||
          String(purchaseId || '') !== String(ref.purchaseId || '')
        ) {
          changed = true;
        }
        return {
          ...ref,
          id: live.id,
          purchaseId,
          invoiceNumber,
          invoiceDate,
          amount,
          paidAmount
        };
      });
      if (!changed) return inv;
      listChanged = true;
      return { ...inv, originalInvoices };
    });

    return listChanged ? nextList : list;
  }

  /**
   * Remove one consolidated shell by id. Child FACT-* invoices are kept.
   * Caller should persist via setSupermarketInvoices (re-consolidates ≤1 CON/supplier).
   */
  stripConsolidatedShell(invoices, consolidatedId) {
    const id = consolidatedId != null ? String(consolidatedId) : '';
    if (!id) return this.removeDuplicateInvoices(invoices || []);
    return this.removeDuplicateInvoices(
      (invoices || []).filter((inv) => {
        if (!this.isConsolidatedInvoice(inv)) return true;
        return String(inv?.id) !== id && String(inv?.invoiceNumber) !== id;
      })
    );
  }

  /**
   * Drop ALL consolidated shells (children stay). Useful before a clean rebuild.
   */
  stripAllConsolidatedShells(invoices) {
    return this.flattenToBaseInvoices(invoices);
  }

  /**
   * Normalize: children kept, at most one CON parent per supplier (dedupe duplicates).
   */
  dedupeConsolidatedInvoices(invoices) {
    return this.consolidateInvoicesInMemory(invoices || []);
  }

  /**
   * Automatische Konsolidierung — synchron, gibt finale Rechnungsliste zurück.
   * Regel: nur wenn ≥2 offene Rechnungen desselben Lieferanten; sonst Einzelrechnung direkt zahlbar.
   */
  isConsolidatedInvoice(invoice) {
    return isConsolidatedInvoiceRecord(invoice);
  }

  getInvoiceRemaining(invoice) {
    return resolveInvoiceRemainingAmount(invoice);
  }

  isOpenInvoice(invoice) {
    return this.getInvoiceRemaining(invoice) > 0;
  }

  getSupplierGroupKey(invoice) {
    if (invoice?.supplierId != null && invoice.supplierId !== '') {
      return `id:${String(invoice.supplierId)}`;
    }
    const name = String(invoice?.supplierName || '').trim().toLowerCase();
    return name ? `name:${name}` : '';
  }

  consolidateInvoicesInMemory(invoices) {
    const uniqueInvoices = this.removeDuplicateInvoices(invoices || []);

    const baseInvoices = uniqueInvoices.filter((invoice) => !this.isConsolidatedInvoice(invoice));
    const previousConsolidated = uniqueInvoices.filter((invoice) => this.isConsolidatedInvoice(invoice));

    const openInvoices = baseInvoices.filter((invoice) => this.isOpenInvoice(invoice));

    const openBySupplier = new Map();
    openInvoices.forEach((invoice) => {
      const key = this.getSupplierGroupKey(invoice);
      if (!key) return;
      if (!openBySupplier.has(key)) openBySupplier.set(key, []);
      openBySupplier.get(key).push(invoice);
    });

    const activeConsolidated = [];
    const activeConsolidatedIds = new Set();
    /** One consolidated parent per supplier — duplicate CON shells must not survive. */
    const activeConsolidatedSupplierKeys = new Set();

    const rebuildConsolidated = (existing, childInvoices) => {
      const uniqueChildren = this.removeDuplicateInvoices(childInvoices || []);
      if (uniqueChildren.length < 2) return null;

      const sortedChildren = this.sortInvoicesByNumber(uniqueChildren);
      const supplierId = sortedChildren[0]?.supplierId;
      let consolidated = this.createConsolidatedInvoice(supplierId, sortedChildren);
      if (!consolidated) return null;

      if (existing) {
        consolidated.id = existing.id;
        consolidated.invoiceNumber = existing.invoiceNumber;
        consolidated.createdAt = existing.createdAt;
        consolidated.payments = Array.isArray(existing.payments) ? [...existing.payments] : [];
        consolidated.lastPaymentDate = existing.lastPaymentDate;
      }

      consolidated.paidAmount = sortedChildren.reduce((sum, inv) => sum + (inv.paidAmount || 0), 0);
      consolidated.totalAmount = sortedChildren.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      consolidated.status = consolidated.paidAmount >= (consolidated.totalAmount || 0)
        ? 'paid'
        : (consolidated.paidAmount > 0 ? 'partial' : 'pending');

      return consolidated;
    };

    for (const [supplierKey, supplierOpenInvoices] of openBySupplier.entries()) {
      if (supplierOpenInvoices.length < 2) continue;

      const uniqueOpen = this.removeDuplicateInvoices(supplierOpenInvoices);
      if (uniqueOpen.length < 2) continue;

      // Prefer the shell that already carries payment state when duplicates exist
      const existingCandidates = previousConsolidated.filter(
        (inv) => this.getSupplierGroupKey(inv) === supplierKey
      );
      const existing =
        existingCandidates.find(
          (inv) =>
            (Number(inv.paidAmount) || 0) > 0 ||
            (Array.isArray(inv.payments) && inv.payments.length > 0)
        ) || existingCandidates[0];

      let childrenForGroup = uniqueOpen;
      if (existing?.originalInvoices?.length) {
        const coversAllOpen = uniqueOpen.every((inv) =>
          (existing.originalInvoices || []).some((ref) => refMatchesInvoice(ref, inv))
        );
        if (coversAllOpen) {
          const indexes = indexBaseInvoices(baseInvoices);
          const referencedChildren = [];
          const seen = new Set();
          for (const ref of existing.originalInvoices) {
            const live = resolveLiveChildInvoiceShared(ref, baseInvoices, indexes);
            if (!live || seen.has(String(live.id))) continue;
            seen.add(String(live.id));
            referencedChildren.push(live);
          }
          if (referencedChildren.length >= 2) {
            childrenForGroup = referencedChildren;
          }
        }
      }

      const consolidated = rebuildConsolidated(existing, childrenForGroup);
      if (!consolidated) continue;

      activeConsolidated.push(consolidated);
      activeConsolidatedIds.add(consolidated.id);
      activeConsolidatedSupplierKeys.add(supplierKey);
    }

    // Garder une facture consolidée avec paiements partiels même s'il ne reste qu'une ligne ouverte
    for (const existing of previousConsolidated) {
      if (activeConsolidatedIds.has(existing.id)) continue;

      const supplierKey = this.getSupplierGroupKey(existing);
      // Drop duplicate CON shells for a supplier already rebuilt above
      if (supplierKey && activeConsolidatedSupplierKeys.has(supplierKey)) continue;

      const hasPaymentState =
        (existing.paidAmount || 0) > 0 ||
        (Array.isArray(existing.payments) && existing.payments.length > 0);
      if (!hasPaymentState) continue;

      const indexes = indexBaseInvoices(baseInvoices);
      const children = [];
      const seenChildren = new Set();
      for (const ref of existing.originalInvoices || []) {
        const live = resolveLiveChildInvoiceShared(ref, baseInvoices, indexes);
        if (!live || seenChildren.has(String(live.id))) continue;
        seenChildren.add(String(live.id));
        children.push(live);
      }
      if (children.length < 2) continue;

      const consolidated = rebuildConsolidated(existing, children);
      if (!consolidated || this.getInvoiceRemaining(consolidated) <= 0) continue;

      activeConsolidated.push(consolidated);
      activeConsolidatedIds.add(consolidated.id);
      if (supplierKey) activeConsolidatedSupplierKeys.add(supplierKey);
    }

    const result = this.syncConsolidatedOriginalRefs(
      this.removeDuplicateInvoices([...baseInvoices, ...activeConsolidated])
    );

    // Loop-safe: prefer the caller’s array reference when content is unchanged
    if (areInvoicesEquivalent(result, invoices)) {
      return invoices;
    }
    if (areInvoicesEquivalent(result, uniqueInvoices)) {
      return uniqueInvoices;
    }
    return result;
  }

  /**
   * Automatische Konsolidierung nach Rechnungserstellung
   */
  async autoConsolidate(newInvoices, dataContext) {
    try {
      const { supermarketInvoices, setSupermarketInvoices } = dataContext;
      const allInvoices = this.removeDuplicateInvoices([
        ...(supermarketInvoices || []),
        ...(newInvoices || [])
      ]);
      const consolidatedList = this.consolidateInvoicesInMemory(allInvoices);

      if (setSupermarketInvoices) {
        setSupermarketInvoices(consolidatedList);
      }

      return consolidatedList;
    } catch (error) {
      console.warn('[AUTO INVOICE] autoConsolidate failed:', error?.message || error);
      return dataContext?.supermarketInvoices || [];
    }
  }

  /**
   * Erstellt konsolidierte Rechnung für einen Lieferanten
   */
  createConsolidatedInvoice(supplierId, invoices) {
    // Money-SSOT: Summe der gespeicherten Kinder-totalAmount / paidAmount
    const totalAmount = this.calculateConsolidatedTotalFromOriginalInvoices(invoices);
    const totalPaidAmount = invoices.reduce((sum, inv) => sum + (inv.paidAmount || 0), 0);
    const supplierName = invoices[0]?.supplierName || 'Unknown Supplier';
    // Stable id per supplier so id-merge collapses duplicate shells even if consolidate is skipped once
    const groupKey = this.getSupplierGroupKey({ supplierId, supplierName });
    const stableId = groupKey
      ? `consolidated_${String(groupKey).replace(/[^a-zA-Z0-9]+/g, '_')}`
      : `consolidated_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      id: stableId,
      invoiceNumber: isoStandardIdGenerator.generateConsolidatedInvoiceNumber(),
      supplierId,
      supplierName,
      totalAmount,
      paidAmount: totalPaidAmount,
      status: this.deriveInvoiceStatus(totalAmount, totalPaidAmount),
      type: 'consolidated',
      isConsolidated: true,
      createdAt: new Date().toISOString(),
      invoiceDate: new Date().toISOString(),
      notes: `Consolidated invoice for ${invoices.length} original invoices`,
      originalInvoices: invoices
        .map(inv => ({
          id: inv.id,
          purchaseId: inv.purchaseId || inv.originalTransactionId || null,
          invoiceNumber: inv.invoiceNumber || inv.id, // same number as Paiement page
          invoiceDate: inv.invoiceDate || inv.createdAt || new Date().toISOString(),
          amount: inv.totalAmount || inv.total || 0,
          paidAmount: inv.paidAmount || 0
        }))
        .filter((inv, index, arr) => 
          // ✅ FIX: Entferne Duplikate in originalInvoices
          arr.findIndex(i => i.id === inv.id) === index
        )
        .sort((a, b) => {
          // ✅ SORTIERUNG: Sortiere nach Rechnungsnummer in aufsteigender Reihenfolge
          // Extrahiere die Nummer aus dem Format FACT-2025-000001
          const extractNumber = (invoiceNumber) => {
            if (!invoiceNumber) return 0;
            const match = invoiceNumber.match(/(\d+)$/);
            return match ? parseInt(match[1], 10) : 0;
          };
          
          const numA = extractNumber(a.invoiceNumber);
          const numB = extractNumber(b.invoiceNumber);
          
          // Wenn Nummern gleich sind, sortiere nach Datum
          if (numA === numB) {
            const dateA = new Date(a.invoiceDate || 0);
            const dateB = new Date(b.invoiceDate || 0);
            return dateA - dateB;
          }
          
          return numA - numB; // Aufsteigende Sortierung
        }),
      // Leeres items Array um Doppelberechnung zu vermeiden
      items: [],
      // Explizit subtotal = totalAmount setzen für konsolidierte Rechnungen
      subtotal: totalAmount,
      tax: 0
    };
  }

  /**
   * Verarbeitet alle ausstehenden Transaktionen
   * WICHTIG: Nur Purchases werden zu Rechnungen, nicht Orders!
   * Orders müssen erst zu Purchases werden, bevor sie Rechnungen werden können.
   */
  async processAllPendingTransactions(dataContext) {
    return this.processPurchasesForInvoices(dataContext, null);
  }

  /**
   * Erstellt Facture(n) für bestimmte Achat-IDs (empfohlen nach neuem Purchase).
   * @param {object} dataContext
   * @param {string[]|null} purchaseIds — nur diese purchaseIds; null = alle
   */
  async processPurchasesForInvoices(dataContext, purchaseIds = null) {
    const groupedPurchases = this.groupPurchasesByPurchaseId(dataContext.purchases || []);
    const idSet =
      purchaseIds == null
        ? null
        : new Set(purchaseIds.map((x) => String(x)));

    const filtered = idSet
      ? groupedPurchases.filter(
          (g) =>
            idSet.has(String(g.id)) || idSet.has(String(g.purchaseId))
        )
      : groupedPurchases;

    const allTransactions = filtered.map((p) => ({
      ...p,
      type: 'purchase',
      date: p.date || p.purchaseDate || p.deliveryDate,
      purchaseDate: p.purchaseDate || p.deliveryDate || p.date
    }));

    return this.createInvoicesFromTransactions(allTransactions, dataContext);
  }

  /**
   * Lädt Achat- und Rechnungskontext aus KV / dataContext.
   */
  _loadPurchaseInvoiceContext(dataContext, purchaseId) {
    const businessId = dataContext.businessId || getCurrentBusinessId();
    if (!businessId) {
      throw new Error('businessId manquant');
    }

    let purchases = dataContext.purchases || [];
    let supermarketInvoices = dataContext.supermarketInvoices || [];
    let suppliers = dataContext.suppliers || [];

    const purchasesFromKv = readPurchasesSyncForBusiness(businessId);
    if (purchasesFromKv.length > 0) {
      purchases = purchasesFromKv;
    }
    const parsed = readBestBusinessMergedPayload(businessId).parsed;
    if (parsed) {
      if (purchasesFromKv.length === 0 && parsed.purchases) {
        purchases = parsed.purchases;
      }
      supermarketInvoices = parsed.supermarketInvoices || supermarketInvoices;
      suppliers = parsed.suppliers || suppliers;
    }

    const pid = String(purchaseId);
    const grouped = this.groupPurchasesByPurchaseId(purchases);
    const group = grouped.find(
      (g) => String(g.id) === pid || String(g.purchaseId) === pid
    );
    if (!group) {
      throw new Error(`Achat introuvable: ${purchaseId}`);
    }

    const transaction = {
      ...group,
      type: 'purchase',
      id: group.id || pid,
      purchaseId: group.purchaseId || group.id || pid,
      date: group.date || group.purchaseDate || group.deliveryDate,
      purchaseDate: group.purchaseDate || group.deliveryDate || group.date
    };

    return { businessId, purchases, supermarketInvoices, suppliers, pid, group, transaction };
  }

  findInvoiceForPurchase(supermarketInvoices, purchaseId) {
    const pid = String(purchaseId);
    return (
      (supermarketInvoices || []).find(
        (inv) =>
          !this.isConsolidatedInvoice(inv) &&
          (String(inv.purchaseId) === pid || String(inv.originalTransactionId) === pid)
      ) || null
    );
  }

  deriveInvoiceStatus(totalAmount, paidAmount) {
    const total = Number(totalAmount) || 0;
    const paid = Number(paidAmount) || 0;
    if (paid <= 0) return 'pending';
    if (paid >= total) return 'paid';
    return 'partial';
  }

  /**
   * Aktualisiert eine bestehende Facture nach Achat-Änderung.
   * Behält paidAmount, payments und Teilzahlungsstatus bei.
   */
  patchInvoiceFromPurchase(existingInvoice, transaction, supplier) {
    const items = this.generateInvoiceItems(transaction);
    const computedTotal = items.reduce(
      (sum, item) => sum + (Number(item.totalPrice) || 0),
      0
    );
    const totalAmount =
      computedTotal > 0
        ? computedTotal
        : Number(transaction.totalAmount || transaction.total || 0);

    const paidAmount = Number(existingInvoice.paidAmount) || 0;
    const supplierName =
      supplier?.name ||
      supplier?.supplierName ||
      supplier?.company ||
      existingInvoice.supplierName ||
      'Unknown Supplier';

    return {
      ...existingInvoice,
      supplierId: transaction.supplierId ?? existingInvoice.supplierId,
      supplierName,
      supplierAddress: supplier?.address || existingInvoice.supplierAddress || '',
      supplierPhone: supplier?.phone || existingInvoice.supplierPhone || '',
      totalAmount,
      items,
      paidAmount,
      payments: Array.isArray(existingInvoice.payments) ? existingInvoice.payments : [],
      lastPaymentDate: existingInvoice.lastPaymentDate || null,
      status: this.deriveInvoiceStatus(totalAmount, paidAmount),
      purchaseId: transaction.purchaseId || existingInvoice.purchaseId,
      originalTransactionId:
        existingInvoice.originalTransactionId || transaction.purchaseId || transaction.id,
      updatedAt: new Date().toISOString(),
      invoiceDate:
        transaction.purchaseDate ||
        transaction.deliveryDate ||
        transaction.date ||
        existingInvoice.invoiceDate
    };
  }

  async _persistSupermarketInvoicesList(businessId, mergedInvoices, setSupermarketInvoices) {
    // Use persistSupermarketInvoices so Hybrid ops outbox enqueue runs (not bare mutatePayload).
    const finalList = await getInvoicesRepository().persistSupermarketInvoices(
      businessId,
      this.consolidateInvoicesInMemory(this.removeDuplicateInvoices(mergedInvoices))
    );

    if (setSupermarketInvoices) {
      setSupermarketInvoices(finalList);
    }

    // Notify Manager / Bilan / Payment surfaces (typed finance fact)
    try {
      const { publishSupplierInvoiceChanged } = await import(
        '../infrastructure/events/legacyEventBridge'
      );
      const { afterSupplierFinanceCommit } = await import('./cacheReadModel');
      publishSupplierInvoiceChanged({
        businessId,
        invoicesCount: Array.isArray(finalList) ? finalList.length : 0,
        source: 'invoiceService.persist'
      });
      afterSupplierFinanceCommit('supplier_invoice_changed');
    } catch (notifyErr) {
      console.warn('[invoiceService] invoice change notify:', notifyErr?.message || notifyErr);
    }

    return finalList;
  }

  /**
   * Synchronisiert Facture mit geändertem Achat (Menge, Preise, Produkte, Fournisseur).
   */
  async updateInvoiceForPurchase(dataContext, purchaseId) {
    const ctx = this._loadPurchaseInvoiceContext(dataContext, purchaseId);
    const { businessId, supermarketInvoices, suppliers, pid } = ctx;
    let { transaction } = ctx;

    // Frische Purchases aus dem Aufrufer bevorzugen (direkt nach Achat-Edit)
    if (Array.isArray(dataContext.purchases)) {
      const hasReceipt = dataContext.purchases.some(
        (p) => String(p.purchaseId) === pid
      );
      if (hasReceipt) {
        const grouped = this.groupPurchasesByPurchaseId(dataContext.purchases);
        const group = grouped.find(
          (g) => String(g.id) === pid || String(g.purchaseId) === pid
        );
        if (group) {
          transaction = {
            ...group,
            type: 'purchase',
            id: group.id || pid,
            purchaseId: group.purchaseId || group.id || pid,
            date: group.date || group.purchaseDate || group.deliveryDate,
            purchaseDate: group.purchaseDate || group.deliveryDate || group.date
          };
        }
      }
    }

    this.productsRef = Array.isArray(dataContext.products) ? dataContext.products : [];

    const existing = this.findInvoiceForPurchase(supermarketInvoices, pid);
    if (!existing) {
      return this.createInvoiceForPurchase(dataContext, purchaseId);
    }

    const supplier = this.resolveSupplier(transaction.supplierId, suppliers);
    const patched = this.patchInvoiceFromPurchase(existing, transaction, supplier);

    const merged = (supermarketInvoices || []).map((inv) =>
      inv.id === patched.id ? patched : inv
    );

    const finalList = await this._persistSupermarketInvoicesList(
      businessId,
      merged,
      dataContext.setSupermarketInvoices
    );

    return [finalList.find((inv) => inv.id === patched.id) || patched];
  }

  /**
   * Erstellt genau eine Facture für einen Achat (robust, für handleAddPurchase).
   */
  async createInvoiceForPurchase(dataContext, purchaseId) {
    const ctx = this._loadPurchaseInvoiceContext(dataContext, purchaseId);
    const { businessId, supermarketInvoices, suppliers, pid, transaction } = ctx;

    const existing = this.findInvoiceForPurchase(supermarketInvoices, pid);
    if (existing) {
      return [existing];
    }

    this.productsRef = Array.isArray(dataContext.products) ? dataContext.products : [];
    const supplier = this.resolveSupplier(transaction.supplierId, suppliers);
    const invoice = this.createInvoice(transaction, supplier);
    if (!invoice) {
      throw new Error('Impossible de créer la facture');
    }

    const mergedInvoices = [...(supermarketInvoices || []), invoice];
    await this._persistSupermarketInvoicesList(
      businessId,
      mergedInvoices,
      dataContext.setSupermarketInvoices
    );

    return [invoice];
  }

  // ✅ FIX: Entferne Duplikate aus Rechnungsliste
  removeDuplicateInvoices(invoices) {
    const seen = new Set();
    const unique = [];
    
    invoices.forEach(invoice => {
      // Verwende ID als Hauptschlüssel für Duplikaterkennung
      const key = invoice.id || invoice.invoiceNumber || `${invoice.supplierId}_${invoice.createdAt}`;
      
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(invoice);
      } else {
      }
    });
    
    return unique;
  }

  // ✅ FIX: Summe der gespeicherten Kinder-totalAmount (Money-SSOT, wie Payment/Bilan)
  calculateConsolidatedTotalFromOriginalInvoices(invoices) {
    let totalAmount = 0;
    
    invoices.forEach(invoice => {
      if (invoice.isConsolidated || invoice.type === 'consolidated') {
        return;
      }
      totalAmount += Number(invoice.totalAmount ?? invoice.total ?? 0) || 0;
    });
    
    return totalAmount;
  }

  /**
   * Reset der verarbeiteten Transaktionen
   */
  resetProcessed() {
    this.processedTransactions.clear();
  }

  /**
   * Gruppiert Purchases nach purchaseId
   */
  groupPurchasesByPurchaseId(purchases) {
    const grouped = new Map();
    
    purchases.forEach(purchase => {
      const purchaseId = purchase.purchaseId || purchase.id;
      if (!grouped.has(purchaseId)) {
        grouped.set(purchaseId, {
          id: purchaseId,
          purchaseId,
          supplierId: purchase.supplierId,
          totalAmount: 0,
          items: [],
          date: purchase.date,
          deliveryDate: purchase.deliveryDate,
          purchaseDate: purchase.purchaseDate || purchase.deliveryDate || purchase.date
        });
      }
      
      const group = grouped.get(purchaseId);
      const lineItems = this.extractRawItems(purchase);
      if (lineItems.length > 0) {
        lineItems.forEach(li => {
          const norm = this.normalizeItem(li);
          group.items.push(norm);
          group.totalAmount += norm.totalPrice || (norm.quantity * norm.unitPrice);
        });
      } else {
        // Single-record purchase format (one record per product)
        const fallbackItem = {
          productId: purchase.productId || purchase.id,
          productName: purchase.productName, // Prefer explicit productName from Purchases table
          name: purchase.productName || purchase.name || `Achat ${purchaseId}`,
          quantity: purchase.quantity || 1,
          unitPrice: purchase.purchaseUnitPrice || purchase.unitPrice || purchase.price || 0,
          totalPrice: purchase.total || purchase.totalAmount || 0
        };
        const norm = this.normalizeItem(fallbackItem);
        group.items.push(norm);
        group.totalAmount += norm.totalPrice || (norm.quantity * norm.unitPrice);
      }
    });

    return Array.from(grouped.values());
  }

  /**
   * Gruppiert Orders nach orderId
   */
  groupOrdersByOrderId(orders) {
    const grouped = new Map();
    
    orders.forEach(order => {
      const orderId = order.orderId || order.id;
      if (!grouped.has(orderId)) {
        grouped.set(orderId, {
          id: orderId,
          supplierId: order.supplierId,
          totalAmount: 0,
          items: [],
          orderDate: order.orderDate || order.deliveryDate
        });
      }
      
      const group = grouped.get(orderId);
      const lineItems = this.extractRawItems(order);
      if (lineItems.length > 0) {
        lineItems.forEach(li => {
          const norm = this.normalizeItem(li);
          group.items.push(norm);
          group.totalAmount += norm.totalPrice || (norm.quantity * norm.unitPrice);
        });
      } else {
        // Single-record order format fallback
        const fallbackItem = {
          productId: order.productId || order.id,
          productName: order.productName,
          name: order.productName || order.name || `Commande ${orderId}`,
          quantity: order.quantity || 1,
          unitPrice: order.purchaseUnitPrice || order.unitPrice || order.price || 0,
          totalPrice: order.total || order.totalAmount || 0
        };
        const norm = this.normalizeItem(fallbackItem);
        group.items.push(norm);
        group.totalAmount += norm.totalPrice || (norm.quantity * norm.unitPrice);
      }
    });

    return Array.from(grouped.values());
  }

  /**
   * Sortiert Rechnungen nach Rechnungsnummer in aufsteigender Reihenfolge
   * Format: FACT-2025-000001, FACT-2025-000002, etc.
   */
  sortInvoicesByNumber(invoices) {
    return [...invoices].sort((a, b) => {
      const cmp = compareInvoiceNumbers(a.invoiceNumber || a.id, b.invoiceNumber || b.id);
      if (cmp !== 0) return cmp;
      const dateA = new Date(a.invoiceDate || a.createdAt || 0);
      const dateB = new Date(b.invoiceDate || b.createdAt || 0);
      return dateA - dateB;
    });
  }
}

export default new InvoiceService();
